import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AuthProvider from "./AuthProvider";
import { AuthModule } from "./AuthProvider";
import { AuthState } from "./AuthStateProvider";
import {
  useLoginRequest,
  useLogoutRequest,
  useRefreshTokenRequest,
} from "../../api/auth/auth";

interface AuthActions {
  handleLoginActionClick: (req: AuthModule.UserRequest) => void;
  handleLogoutActionClick: () => void;
  handleRefreshAccessToken: () => void;
}

export const AuthActionsContext = React.createContext<
  Omit<AuthActions, "handleRefreshAccessToken">
>(undefined!);

export function useAuthActions() {
  const context = React.useContext(AuthActionsContext);

  if (context == null) {
    throw new Error(
      `Invocations to ${useAuthActions.name} should be within a node that is a child of ${AuthProvider.name}`
    );
  }

  return context;
}

export const useAuthenticationActions = (authState: AuthState) => {
  const navigate = useNavigate();
  const logoutProcessedRef = React.useRef(false);
  const loginProcessedRef = React.useRef(false);

  const loginRequest = useLoginRequest();
  
  const handleLoginActionClick = React.useCallback(
    (req: AuthModule.UserRequest) => {
      loginProcessedRef.current = false; // Reset the flag when initiating login
      loginRequest.mutate(req);
    },
    [loginRequest.mutate]
  );

  React.useEffect(() => {
    if (loginRequest.isSuccess && loginRequest.data && !loginProcessedRef.current) {
      loginProcessedRef.current = true; // Set flag to prevent multiple executions
      authState.handleUpdateAuthState(loginRequest.data);
      toast.success("Successfully logged in user", {
        toastId: "login-success", // Prevent duplicate toasts
      });
      navigate("/"); // Navigate to the main app after successful login
    }
  }, [loginRequest.isSuccess, loginRequest.data, authState, navigate]);

  React.useEffect(() => {
    if (loginRequest.isError && !loginProcessedRef.current) {
      loginProcessedRef.current = true; // Set flag to prevent multiple error toasts
      
      // Extract error message from API response if available
      let errorMessage = "Login failed. Please check your credentials.";
      
      const axiosError = loginRequest.error as any;
      if (axiosError?.response?.data) {
        const responseData = axiosError.response.data;
        
        // Check various possible error message formats
        if (responseData.message) {
          errorMessage = responseData.message;
        } else if (responseData.error) {
          errorMessage = responseData.error;
        } else if (responseData.detail) {
          errorMessage = responseData.detail;
        } else if (responseData.errors && Array.isArray(responseData.errors)) {
          errorMessage = responseData.errors.join(', ');
        } else if (typeof responseData === 'string') {
          errorMessage = responseData;
        } else {
          // Fallback: show the HTTP status text if available
          errorMessage = axiosError.response.statusText || errorMessage;
        }
      } else if (axiosError?.message) {
        errorMessage = axiosError.message;
      }

      // Log error for debugging (production-safe)
      console.error("Login failed:", axiosError?.response?.status, errorMessage);

      toast.error(errorMessage, {
        toastId: "login-error", // Prevent duplicate toasts
        autoClose: 8000, // Show longer for error messages
      });
    }
  }, [loginRequest.isError, loginRequest.error]);

  const logoutRequest = useLogoutRequest();
  const handleLogoutActionClick = React.useCallback(() => {
    logoutProcessedRef.current = false; // Reset the flag when initiating logout
    logoutRequest.mutate();
  }, [logoutRequest.mutate]);

  React.useEffect(() => {
    if (logoutRequest.isSuccess && logoutRequest.data && !logoutProcessedRef.current) {
      logoutProcessedRef.current = true; // Set flag to prevent multiple executions
      authState.handleIntentionalLogout(); // Use intentional logout to prevent session expired toast
      toast.success("Successfully logged out", {
        toastId: "logout-success", // Prevent duplicate toasts
      });
      // Use a small delay to ensure state is updated before navigation
      setTimeout(() => {
        navigate("/login", { replace: true });
      }, 100);
    }
  }, [logoutRequest.isSuccess, logoutRequest.data, authState, navigate]);

  const refreshRequest = useRefreshTokenRequest();
  const handleRefreshAccessToken = React.useCallback(async () => {
    const result = await refreshRequest.mutateAsync({
      token: authState.authRef.current.token?.token,
      refreshToken: authState.authRef.current.refreshToken?.token,
    });

    if (result.statusCode !== 200) {
      handleLogoutActionClick();
      toast.error("Please sign in again, your session has expired");
      return undefined;
    }

    const update: typeof authState.state = {
      ...authState.state,
      token: { ...result.data?.token },
      refreshToken: { ...result.data?.refreshToken },
    };
    authState.handleUpdateRefreshTokens(update);

    return result.data?.token?.token;
  }, [refreshRequest.mutate]);

  return {
    isLoginPending: loginRequest.isPending,
    handleLoginActionClick,
    handleLogoutActionClick,
    handleRefreshAccessToken,
  };
};
